import { Card } from '@ipfabric/ui-components/Card/Card';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { Button } from 'components/Button/Button';
import { ButtonLink } from 'components/Button/ButtonLink';
import { useLocale } from 'containers/App/LocaleContext';
import { type MouseEvent, useRef, useState } from 'react';
import type { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import { Navigate, useNavigate, useParams } from 'react-router';
import { WebHookForm, type WebHookFormHandle } from './WebHookForm';
import type {
  EventType,
  WebHookId,
  WebHookRowData,
  WebHookType,
} from './WebHooksState';
import type { SaveWebHookCommand } from './commands/saveWebHook';
import { WEBHOOKS_BASE_URL, buildWebhookDeliveriesLink } from './constants';
import { getInitialValues } from './helpers/getInitialValues';

interface Props {
  caCertOptions: { label: string; value: string }[];
  eventTypes: EventType[];
  reloadCACerts: () => void | Promise<void>;
  saveWebHook: (command: SaveWebHookCommand) => void;
  type: 'create' | 'edit';
  webhooks: WebHookRowData[];
  detailLoadingState: LoadingState;
}

export type RightPanelContentType = {
  general: string;
  payload: string;
  type: WebHookType;
} | null;

const getWebHookById = (
  webhooks: WebHookRowData[],
  idToSearch: string,
): WebHookRowData | null =>
  webhooks.find(({ id }) => id === idToSearch) || null;

export const WebHookDetail: FC<Props> = ({
  caCertOptions,
  eventTypes,
  reloadCACerts,
  saveWebHook,
  type,
  webhooks,
  detailLoadingState,
}) => {
  const { webhookId } = useParams<{ webhookId: WebHookId }>();
  const navigate = useNavigate();
  const msg = useLocale();
  const [rightPanelContent, setRightPanelContent] =
    useState<RightPanelContentType | null>(null);
  const formRef = useRef<WebHookFormHandle>(null);

  const webhook =
    type === 'edit' && webhookId !== undefined
      ? getWebHookById(webhooks, webhookId)
      : null;

  const submitForm = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const handleResetRightPanelContent = (e: MouseEvent) => {
    e.preventDefault();
    setRightPanelContent(null);
  };

  const makeSetRightPanelContent =
    (content: RightPanelContentType) => (e: MouseEvent) => {
      e.preventDefault();
      setRightPanelContent(content);
    };

  const close = () => navigate(WEBHOOKS_BASE_URL);

  // Unknown webhook id (stale deep link): close back to the list rather than
  // render a full-page not-found over it.
  if (type === 'edit' && !webhook) {
    return <Navigate to={WEBHOOKS_BASE_URL} replace />;
  }

  const initialValues = getInitialValues(webhook);

  return (
    <SidePanel
      // `large` is the 660px preset, which matches the Figma panel width (it is
      // ~half the content area at the design's reference width).
      size='large'
      closeOnBackdropClick={false}
      onClose={close}
      title={type === 'edit' ? 'Edit webhook' : 'New webhook'}
      footer={
        <div className='flex gap-2'>
          <Button
            variant='primary'
            onClick={submitForm}
            type='submit'
            loading={detailLoadingState === 'LOADING'}
            dataTestId='webhook-editor-submit'
          >
            {type === 'edit'
              ? msg.settings.webHooks.form.btnSave
              : msg.settings.webHooks.btnAdd}
          </Button>
          <ButtonLink
            variant='secondary'
            accent='positive'
            to={WEBHOOKS_BASE_URL}
          >
            {msg.settings.webHooks.btnClose}
          </ButtonLink>
        </div>
      }
    >
      <Card className='p' data-testid='webhook-editor'>
        <WebHookForm
          caCertOptions={caCertOptions}
          eventTypes={eventTypes}
          handleResetRightPanelContent={handleResetRightPanelContent}
          initialValues={initialValues}
          makeSetRightPanelContent={makeSetRightPanelContent}
          ref={formRef}
          reloadCACerts={reloadCACerts}
          saveWebHook={saveWebHook}
          selectedHelp={rightPanelContent?.type}
        />
        {rightPanelContent && (
          <div className='mt-8'>
            <h3 className='no-margin'>{rightPanelContent.general}</h3>
            <ReactMarkdown className='mt-4'>
              {rightPanelContent.payload}
            </ReactMarkdown>
          </div>
        )}
        {webhook && webhookId && (
          <div className='mt-12'>
            <h4 className='no-margin'>
              <strong>
                {msg.settings.webHooks.deliveries.recentDeliveries}
              </strong>
            </h4>
            <p className='mt-1'>
              {msg.settings.webHooks.deliveries.recentDeliveriesNote}
            </p>
            <div className='mt-4'>
              <ButtonLink
                variant='secondary'
                accent='positive'
                to={buildWebhookDeliveriesLink(webhook.name)}
              >
                View all deliveries
              </ButtonLink>
            </div>
          </div>
        )}
      </Card>
    </SidePanel>
  );
};
